import { useEffect, useState } from "react";
import { Alert, Button, Drawer } from "@heroui/react";
import { DirectionChip, Segmented } from "@/components/kit";
import { cn } from "@/lib/utils";
import { useDayRiskStatus } from "../../hooks/useDayRisk";
import { PriceField } from "../daytrader/RiskGuard";
import { MAX_COST_R, ROUND_TRIP_COST, parseNum } from "../daytrader/dayTrader";
import {
  ENTRY_TYPE_META, MIN_RR, TRADES_CHANGED, fmt, pct, ticketDefaults,
  type DeskRow, type OpenSignal, type TicketDefaults, type TradeMode,
} from "./deskData";

/** Remembers Practice vs Real between tickets. */
const MODE_PREF = "vp.trade.mode";

export interface TicketRequest {
  symbol: string;
  side: "LONG" | "SHORT";
}

/**
 * Track a trade from the chart. Pre-filled from what the engine says about the
 * coin (signal / early in a zone / own idea), sized to the one risk-per-trade
 * setting (Settings › Account: the same figure the safety limit uses). It only
 * records the trade for the win/loss stats; it never places an order on an
 * exchange. When the desk is LOCKED for the day, a real trade can still be
 * logged (the journal must stay honest) but it is marked as breaking the limit.
 */
export function TradeTicket({
  request, price, row, openSignal, atr, onClose, onTracked,
}: {
  request: TicketRequest | null;
  /** Live price of the coin. */
  price: number | null;
  row?: DeskRow | null;
  openSignal?: OpenSignal | null;
  atr: number | null;
  onClose: () => void;
  onTracked: (message: string) => void;
}) {
  const { status: guard, error: guardError } = useDayRiskStatus();
  const [mode, setMode] = useState<TradeMode>(() => {
    try { return localStorage.getItem(MODE_PREF) === "real" ? "real" : "paper"; } catch { return "paper"; }
  });
  const [defaults, setDefaults] = useState<TicketDefaults | null>(null);
  const [entry, setEntry] = useState("");
  const [stop, setStop] = useState("");
  const [tp1, setTp1] = useState("");
  const [tp2, setTp2] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A fresh plan each time the ticket opens (or its first price arrives), not
  // on every price tick: that would wipe what the trader typed.
  const key = request ? `${request.symbol}:${request.side}` : "";
  useEffect(() => {
    if (!request || price == null) { setDefaults(null); return; }
    const d = ticketDefaults(request.side, price, row, openSignal, atr);
    setDefaults(d);
    setEntry(String(d.entry));
    setStop(String(d.stop));
    setTp1(String(d.tp1));
    setTp2(String(d.tp2));
    setError(null);
  }, [key, price == null]);

  const pickMode = (m: TradeMode) => {
    setMode(m);
    try { localStorage.setItem(MODE_PREF, m); } catch { /* ignore */ }
  };

  const side = request?.side ?? "LONG";
  const long = side === "LONG";
  const coin = request?.symbol.replace("USDT", "") ?? "";
  const e = parseNum(entry), s = parseNum(stop), t1 = parseNum(tp1), t2 = parseNum(tp2);
  const dir = long ? 1 : -1;
  // Stop behind the entry, targets ahead of it, target 2 at least as far as target 1.
  const problem =
    e == null || s == null || t1 == null || t2 == null ? "Fill in all four prices."
    : dir * (e - s) <= 0 ? `The stop must be ${long ? "below" : "above"} the entry for a ${long ? "buy" : "sell"}.`
    : dir * (t1 - e) <= 0 ? `Target 1 must be ${long ? "above" : "below"} the entry.`
    : dir * (t2 - t1) < 0 ? "Target 2 must be at or past target 1."
    : null;
  const risk = e != null && s != null ? Math.abs(e - s) : 0;
  // An Against-BTC signal is sized at half the usual risk.
  const riskScale = defaults?.riskScale ?? 1;
  const fullRiskUsd = guard?.riskPerTradeUsd ?? 0;
  const riskUsd = fullRiskUsd * riskScale;
  const units = risk > 0 ? riskUsd / risk : 0;
  // Playbook › 5. Risk: the daily limit is final. Practice trades never count.
  const locked = mode === "real" && guard?.locked === true;
  const rr1 = !problem && risk > 0 ? Math.abs(t1! - e!) / risk : null;
  const rr2 = !problem && risk > 0 ? Math.abs(t2! - e!) / risk : null;
  const costR = !problem && e ? ROUND_TRIP_COST / (risk / e) : null;
  const rel = (p: number | null) => (p != null && e ? pct(((p - e) / e) * 100) : "");

  const submit = async () => {
    if (!request || !defaults || problem) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch("/api/tracked-trades", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          symbol: request.symbol,
          direction: side,
          entryPrice: e,
          stopLoss: s,
          takeProfit1: t1,
          takeProfit2: t2,
          margin: units,
          setupType: defaults.archetype ? `${defaults.entryType}:${defaults.archetype}` : defaults.entryType,
          entryType: defaults.entryType,
          mode,
          timeStopHours: defaults.timeStopHours,
          riskScale,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) throw new Error(json.error || "Couldn't track the trade");
      window.dispatchEvent(new Event(TRADES_CHANGED));
      onTracked(`${coin} ${long ? "buy" : "sell"} tracked (${mode === "paper" ? "practice" : "real"})${json.ruleBreak ? " · marked: broke today's limit" : ""}`);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't track the trade");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer.Backdrop isOpen={request != null} onOpenChange={open => { if (!open) onClose(); }}>
      <Drawer.Content placement="right">
        {/* Width goes on the dialog: the content layer spans the viewport. */}
        <Drawer.Dialog className="w-full sm:w-[27rem]">
          <Drawer.CloseTrigger />
          <Drawer.Header>
            <Drawer.Heading className="flex items-center gap-2">
              <DirectionChip direction={side} />
              {long ? "Buy" : "Sell"} {coin}
              {price != null && <span className="num text-sm font-normal text-muted">now {fmt(price)}</span>}
            </Drawer.Heading>
          </Drawer.Header>
          <Drawer.Body className="space-y-4">
            {!defaults ? (
              <p className="text-sm text-muted">Waiting for the price…</p>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Segmented
                    aria-label="Real money or practice"
                    options={[{ id: "paper", label: "Practice" }, { id: "real", label: "Real money" }]}
                    value={mode}
                    onChange={pickMode}
                  />
                  <p className="text-xs text-muted">
                    {mode === "paper"
                      ? "Practice: no real money. Tracked exactly the same way, so you can test the system first."
                      : guard
                        ? `Real: you placed this trade on your exchange. It counts toward today's limits (${guard.config.maxTradesPerDay} trades, ${guard.config.maxDailyLossR}R lost) and gets Telegram alerts.`
                        : "Real: you placed this trade on your exchange. It counts toward today's limits and gets Telegram alerts."}
                  </p>
                </div>

                {locked && guard && (
                  <Alert status="danger">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>The desk is LOCKED for today</Alert.Title>
                      <Alert.Description>
                        {guard.reason}. The Playbook says stop until the reset at {String(guard.config.resetHourUtc).padStart(2, "0")}:00 UTC.
                        If you already placed this trade, log it anyway: it will be marked as breaking today's limit.
                      </Alert.Description>
                    </Alert.Content>
                  </Alert>
                )}

                <Alert status={defaults.entryType === "signal" ? "success" : defaults.entryType === "early" ? "warning" : "default"}>
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Title>{ENTRY_TYPE_META[defaults.entryType].label}{riskScale < 1 ? " · Against BTC, half risk" : ""}</Alert.Title>
                    <Alert.Description>{defaults.reason}</Alert.Description>
                  </Alert.Content>
                </Alert>

                <div className="grid grid-cols-2 gap-3">
                  <PriceField label="Entry" value={entry} onChange={setEntry} hint="Where you got in" />
                  <PriceField label={`Stop ${rel(s)}`} value={stop} onChange={setStop} hint="Where the idea is wrong" />
                  <PriceField label={`Target 1 ${rel(t1)}`} value={tp1} onChange={setTp1} hint="Half closes here" />
                  <PriceField label={`Target 2 ${rel(t2)}`} value={tp2} onChange={setTp2} hint="The rest closes here" />
                </div>

                {problem ? (
                  <p className="text-sm text-loss">{problem}</p>
                ) : (
                  <div className="space-y-1 rounded-card border border-border bg-surface-secondary p-3 text-xs">
                    <div className="flex justify-between gap-3">
                      <span className="text-muted">
                        {guard
                          ? riskScale < 1
                            ? `Size to risk $${riskUsd.toFixed(2)}: half your usual $${fullRiskUsd.toFixed(2)}, against BTC`
                            : `Size to risk $${riskUsd.toFixed(2)} (${guard.config.riskPerTradePct}% of $${guard.config.accountSize})`
                          : "Loading your risk per trade…"}
                      </span>
                      <span className="num">{fmt(units)} {coin} ≈ ${(units * e!).toLocaleString("en-US", { maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-muted">Reward ÷ risk</span>
                      <span className="num">{rr1!.toFixed(1)}× to target 1 · {rr2!.toFixed(1)}× to target 2</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-muted">Fees + slippage</span>
                      <span className={cn("num", costR! > MAX_COST_R && "text-warning")}>{costR!.toFixed(2)}R of your risk</span>
                    </div>
                  </div>
                )}

                {!problem && rr1! < MIN_RR && (
                  <p className="text-xs text-warning">
                    Target 1 pays only {rr1!.toFixed(1)}× the risk. The engine needs at least {MIN_RR}× before it takes a trade.
                  </p>
                )}
                {!problem && costR! > MAX_COST_R && (
                  <p className="text-xs text-warning">
                    The stop is so close that fees eat {Math.round(costR! * 100)}% of the risk. The engine skips trades over {MAX_COST_R * 100}%.
                  </p>
                )}

                <p className="text-xs leading-relaxed text-muted">
                  Tracked by the plan, on 5-minute candles: stop first; half closes at target 1 and the stop moves to your
                  entry; the rest closes at target 2; if neither happens within {defaults.timeStopHours} hours it closes at
                  the market. Results are in R after fees.
                </p>
                {!guard && guardError && <p className="text-sm text-loss">Couldn't load today's limits: {guardError}</p>}
                {error && <p className="text-sm text-loss">{error}</p>}
              </>
            )}
          </Drawer.Body>
          <Drawer.Footer className="flex-col items-stretch gap-2">
            <Button
              variant="primary"
              fullWidth
              isDisabled={!defaults || problem != null || !guard}
              isPending={saving}
              onPress={submit}
              className={long ? "glow-profit bg-profit text-on-profit" : "glow-loss bg-loss text-on-loss"}
            >
              {locked
                ? "Log anyway (breaks today's limit)"
                : <>Track this {long ? "buy" : "sell"}{mode === "paper" ? " (practice)" : ""}</>}
            </Button>
            <p className="text-center text-2xs text-muted">This records the trade for your stats. It does not place an order on Binance.</p>
          </Drawer.Footer>
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  );
}
